import React from 'react';
import { Box } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { WifiOffIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { CardDashboard } from '@fortress-technology-solutions/fortress-component-library/Molecules_Portal';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';

/**
 * Catches dynamic import (chunk load) failures that occur when the user
 * navigates to a page they haven't visited while offline.
 *
 * This is the expected boundary of our offline support — only pages already
 * loaded in the current session are available offline.
 */
const isChunkLoadError = (error) => {
  const errorMessage = error?.message || '';
  const errorName = error?.name || '';

  return (
    errorName === 'ChunkLoadError' ||
    /loading chunk/i.test(errorMessage) ||
    /failed to fetch dynamically imported module/i.test(errorMessage) ||
    /failed to fetch/i.test(errorMessage) ||
    /network error/i.test(errorMessage) ||
    /loading.*chunk.*failed/i.test(errorMessage)
  );
};

class ChunkLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, hasChunkError: false, error: null };
    this.handleOnline = this.handleOnline.bind(this);
  }

  componentDidMount() {
    window.addEventListener('online', this.handleOnline);
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.hasError &&
      this.props.location?.pathname !== prevProps.location?.pathname
    ) {
      this.setState({ hasError: false, hasChunkError: false, error: null });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
  }

  handleOnline() {
    // When connectivity is restored, reload the page so the failed chunk can be
    // re-fetched. Simply resetting error boundary state won't work because the
    // browser/bundler caches the failed import and re-throws immediately.
    if (this.state.hasChunkError) {
      window.location.reload();
    }
  }

  static getDerivedStateFromError(error) {
    const chunkError = isChunkLoadError(error);
    return { hasError: true, hasChunkError: chunkError, error };
  }

  render() {
    if (this.state.hasError) {
      // Non-chunk errors (e.g. runtime JS bugs) should be handled by the
      // outer DatadogErrorBoundary for Datadog logging and a generic crash UI.
      if (!this.state.hasChunkError) {
        throw this.state.error;
      }

      return (
        <Box sx={{ py: 8 }}>
          <CardDashboard
            Icon={WifiOffIcon}
            title={<FormattedMessage id="app.offline.chunkError.title" />}
            description={
              <FormattedMessage id="app.offline.chunkError.description" />
            }
          />
        </Box>
      );
    }

    return this.props.children;
  }
}

export default withRouter(ChunkLoadErrorBoundary);
